import { useMemo, useState } from 'react';
import { Table, Tag } from 'antd';
import { ModalComponent, Spinner, Title } from '../shared';
import { useDispatch, useSelector } from 'react-redux';
import {
  ILineResource,
  IMusselLineResource,
  IOysterLineResource,
} from '../../entities/farms.entities';
import { loadFarmsData } from '../../store/farms/farms.actions';
import WeatherInfoModal from '../farm-modals/WeatherInfoModal';
import SeedingModal from '../farm-modals/SeedingModal';
import { sendSingleRequest } from '../../apis';
import { IWeatherConditionResource } from '../../entities/general.entities';
import {
  ICycleResource,
  IMusselSeeding,
  IOysterSeeding,
  ISeedingResource,
} from '../../entities/growing.entities';
import { selectLang } from '../../store/ui/ui.selector';
import { translate } from '../../lib/lang.helper';
import {
  directionChar,
  defaultDateFormat,
  displayValueUnit,
} from '../../lib/common.helpers';
import SpatJourneyModal from '../view-modals/SpatJourneyModal';
import {
  SeedDensityRange,
  SeedSizeRange,
  StageLabelSpan,
} from '../CommonViews';
import DotsMenu from '../shared/dropdown-menu/DotsMenu';
import weather_icon from '../../images/weather-icon.png';
import { selectBoats } from '../../store/users/users.selector';
import { hashColor } from '../../entities/util-functions';
import { selectIsAdmin, selectProfile } from '../../store/auth/auth.selector';
import FilesViewModal from '../shared/pictures-wall/FilesViewModal';
import { ACTIVITY_COLORS } from '../../lib/constant';
import { lineLengthLabel } from '../../lib/farm.helpers';

interface IColumn {
  key: string;
  title: string;
  render: (x: any) => JSX.Element;
  width?: number;
}

const SpatColumn = (x: any, businessType: any, unitType: any) =>
  x.is_catch_spat
    ? 'Catch Spat'
    : x.spat_storage
      ? businessType === 'MUSSEL'
        ? `${x.spat_storage.source} - ${displayValueUnit(
            x.spat_amount,
            unitType,
            'weight_kg',
          )}`
        : `${x.spat_storage.source} - ` +
          (x.amount_unit !== 'pcs'
            ? `${x.spat_amount_dz} dz`
            : `${x.spat_amount_pcs} pcs`)
      : '-';

interface IMusselRow extends IMusselSeeding {
  harvested?: number;
}

interface IOysterRow extends IOysterSeeding {
  harvested?: number;
}

interface Props {
  lineData: ILineResource;
  loadLine: () => void;
  cyclePart: ICycleResource;
}

const ExtraSeedingsTable = ({ lineData, loadLine, cyclePart }: Props) => {
  const dispatch = useDispatch();
  const lang = useSelector(selectLang);
  const boats = useSelector(selectBoats);
  const profile = useSelector(selectProfile);
  const isAdmin = useSelector(selectIsAdmin);
  const unitType = profile?.account?.display_unit;

  const [visibleEdit, setVisibleEdit] = useState(false);
  const [visibleDelete, setVisibleDelete] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ISeedingResource>();
  const [disabled, setDisabled] = useState(false);
  const [infoShow, setInfoShow] = useState(false);
  const [infoText, setInfoText] = useState('');
  const [imgPdfFiles, setImgPdfFiles] = useState<string[]>();
  const [selectedWeather, setSelectedWeather] =
    useState<IWeatherConditionResource>();
  const [journeyData, setJourneyData] = useState<any>();

  const totalLength =
      cyclePart.crop_type === 'MUSSEL'
        ? (cyclePart.seedings as IMusselSeeding[]).reduce(
            (p, c) => p + c.line_length,
            0,
          )
        : 0,
    totalBasketCount =
      cyclePart.crop_type === 'OYSTER'
        ? (cyclePart.seedings as IOysterSeeding[]).reduce(
            (p, c) => p + c.basket_count,
            0,
          )
        : 0;

  const showEditModal = (item: ISeedingResource) => {
    setSelectedItem(item);
    setVisibleEdit(true);
  };
  const showDeleteModal = (item: ISeedingResource) => {
    setSelectedItem(item);
    setVisibleDelete(true);
  };
  const handleConfirmDelete = async () => {
    if (!selectedItem) return;
    setDisabled(true);
    const res = await sendSingleRequest(
      {},
      'DELETE',
      `api/farm/line/seeding/${selectedItem.id}`,
      true,
    );
    setDisabled(false);
    if (res.status) {
      loadLine();
      dispatch(loadFarmsData());
      setVisibleDelete(false);
    } else {
      window.alert(translate(lang, res.data?.message ?? 'Unknown error'));
    }
  };
  const openInfo = (comment: any) => {
    setInfoShow(true);
    setInfoText(comment && comment.length > 0 ? comment : 'No comments yet');
  };
  const openImages = (images: string[]) => setImgPdfFiles(images);
  const openJourney = async (spatID: number) => {
    setDisabled(true);
    const res = await sendSingleRequest(
      { from_root: true },
      'GET',
      `api/farm/spats-journey/${spatID}`,
      true,
    );
    setDisabled(false);
    if (res.status) {
      setJourneyData(res.data);
    } else {
      alert(res.data?.message ?? 'Unknown error');
    }
  };
  const getLastAssessment = (spatID: number | undefined) => {
    if (!spatID) return null;
    const assessments = cyclePart.assessments.filter(
      y => y.origin_spat_id === spatID,
    );
    assessments.sort((a, b) => b.assessment_date - a.assessment_date);
    return assessments.length > 0 ? assessments[0] : null;
  };

  const tableRows = useMemo(() => {
    if (cyclePart.crop_type === 'MUSSEL') {
      let result: IMusselRow[] = [
        ...(cyclePart.seedings as IMusselSeeding[]),
      ].map(x => {
        const y = lineData.growing_cycle?.seedings.find(z => z.id === x.id);
        return { ...x, harvested: y?.harvested };
      });
      return result;
    } else if (cyclePart.crop_type === 'OYSTER') {
      let result: IOysterRow[] = [
        ...(cyclePart.seedings as IOysterSeeding[]),
      ].map(x => {
        const y = lineData.growing_cycle?.seedings.find(z => z.id === x.id);
        return { ...x, harvested: y?.harvested };
      });
      return result;
    } else {
      return [];
    }
  }, [cyclePart]);

  const deleteInfoText = useMemo(() => {
    if (!selectedItem) return null;
    const isLastSeeding = cyclePart.seedings.length <= 1;
    const deletingAssessments = isLastSeeding
      ? cyclePart.assessments.length
      : cyclePart.assessments.filter(x => x.batch_seed_id === selectedItem.id)
          .length;
    const deletingMaintenances = isLastSeeding
      ? cyclePart.maintenances.length
      : cyclePart.maintenances.filter(x => x.batch_seed_id === selectedItem.id)
          .length;
    const deletingInventoryManages = isLastSeeding
      ? cyclePart.inventory_seeds.length
      : cyclePart.inventory_seeds.filter(
          x => x.batch_seed_id === selectedItem.id,
        ).length;
    const harvests = isLastSeeding
      ? cyclePart.harvests.length
      : cyclePart.harvests.filter(x =>
          x.harvest_groups?.some(y => y.batch_seed_id === selectedItem.id),
        ).length;
    const rows: { label: string; count: number; color: string }[] = [
      {
        label: translate(
          lang,
          '%s assessments will be removed',
          deletingAssessments,
        ),
        count: deletingAssessments,
        color: ACTIVITY_COLORS.assessment,
      },
      {
        label: translate(
          lang,
          '%s maintenances will be removed',
          deletingMaintenances,
        ),
        count: deletingMaintenances,
        color: ACTIVITY_COLORS.maintenance,
      },
      {
        label: translate(
          lang,
          '%s inventory management entries will be removed',
          deletingInventoryManages,
        ),
        count: deletingInventoryManages,
        color: ACTIVITY_COLORS.inventory_seed,
      },
      {
        label: translate(lang, '%s harvests will be removed', harvests),
        count: harvests,
        color: ACTIVITY_COLORS.harvest,
      },
    ];
    return (
      <div style={{ fontSize: '14px' }}>
        <div
          style={{
            color: ACTIVITY_COLORS.danger,
            fontWeight: 600,
            marginBottom: '10px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <span style={{ fontSize: '16px' }}>⚠</span>
          {translate(lang, 'These data will be removed')}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {rows.map(({ label, count, color }) => (
            <div
              key={label}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '6px 10px',
                borderLeft: `3px solid ${color}`,
                borderRadius: '3px',
                background: '#fafafa',
                opacity: count === 0 ? 0.4 : 1,
              }}
            >
              <span
                style={{
                  minWidth: '24px',
                  fontWeight: 700,
                  color,
                  textAlign: 'center',
                }}
              >
                {count}
              </span>
              <span style={{ color: '#444' }}>{label}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }, [selectedItem, cyclePart, lang]);

  const tableColumns = () => {
    let columns: IColumn[] = [
      {
        key: 'id',
        title: 'ID',
        render: (x: IMusselRow | IOysterRow) => <span>{x.id}</span>,
      },
      {
        key: 'date_seed',
        title: translate(lang, 'Date seeded'),
        render: (x: ISeedingResource) => {
          const lastAss = getLastAssessment(x.spat_storage?.id);
          const boat = boats.find(b => b.id === x.boat_id);
          return (
            <>
              <span>{defaultDateFormat(x.planned_date_seed)}</span>
              {x.verifier && (
                <div className='verifier-name'>
                  {translate(lang, 'Verified by %s', x.verifier.name)}
                </div>
              )}
              {boat && (
                <div
                  className='verifier-name'
                  style={{ color: hashColor(boat.reg_number) }}
                >
                  {translate(lang, 'on vessel %s', boat.name)}
                </div>
              )}
              {lastAss && (
                <>
                  <br />
                  <span>{' ↓ '}</span>
                  <br />
                  <span>{defaultDateFormat(lastAss.assessment_date)}</span>
                </>
              )}
            </>
          );
        },
      },
    ];
    if (cyclePart.crop_type === 'MUSSEL') {
      columns.push({
        key: 'line_length',
        title: translate(
          lang,
          (lineData as IMusselLineResource).line_type === 'LONGLINE'
            ? 'Longline length'
            : 'Number of droppers',
        ),
        render: (x: IMusselRow) => {
          let result =
            tableRows.length === 1
              ? displayValueUnit(
                  x.line_length,
                  unitType,
                  lineData.line_type === 'LONGLINE' ? 'distance_m' : 'droppers',
                )
              : `${displayValueUnit(
                  x.line_length,
                  unitType,
                  lineData.line_type === 'LONGLINE' ? 'distance_m' : 'droppers',
                )} - ${((x.line_length / totalLength) * 100).toFixed(2)}%`;
          if (x.harvested && x.harvested < x.line_length) {
            return (
              <>
                <span style={{ color: ACTIVITY_COLORS.seeding }}>{result}</span>
                <br />
                <span style={{ color: ACTIVITY_COLORS.harvest }}>
                  {translate(
                    lang,
                    '_harvested_avail',
                    lineLengthLabel(
                      cyclePart.crop_type,
                      lineData.line_type,
                      lineData.farming_method,
                      unitType,
                      x.harvested,
                    ),
                    lineLengthLabel(
                      cyclePart.crop_type,
                      lineData.line_type,
                      lineData.farming_method,
                      unitType,
                      x.line_length - x.harvested,
                    ),
                  )}
                </span>
              </>
            );
          }
          return <span>{result}</span>;
        },
      });
    } else if (cyclePart.crop_type === 'OYSTER') {
      columns.push({
        key: 'basket_count',
        title: translate(
          lang,
          'Number of %s',
          (lineData as IOysterLineResource).farming_method,
        ),
        render: (x: IOysterRow) => (
          <>
            {tableRows.length === 1 ? (
              <span>{x.basket_count}</span>
            ) : (
              <span>
                {`${x.basket_count} - ${(
                  (x.basket_count / totalBasketCount) *
                  100
                ).toFixed(2)}%`}
              </span>
            )}
            <span>
              {!!x.harvested && x.harvested < x.basket_count && (
                <span>
                  <br />
                  {translate(
                    lang,
                    '_harvested_avail',
                    x.harvested,
                    x.basket_count - x.harvested,
                  )}
                </span>
              )}
            </span>
          </>
        ),
      });
    }
    columns.push(
      {
        key: 'season_name',
        title: translate(lang, 'Season'),
        render: (x: ISeedingResource) => <span>{x.season_name}</span>,
      },
      {
        key: 'taxonomy_groups',
        title: translate(lang, 'Taxonomy'),
        render: (x: ISeedingResource) => (
          <div className='d-flex flex-col gap-4'>
            {x.taxonomy_groups?.map(t => (
              <Tag key={t.id} color={hashColor(t.name)}>
                {t.name}
              </Tag>
            ))}
          </div>
        ),
      },
      {
        key: 'spat_storage',
        title: translate(lang, 'Spat storage'),
        render: (x: ISeedingResource) => (
          <span
            onClick={() => x.spat_storage && openJourney(x.spat_storage.id)}
          >
            {SpatColumn(x, cyclePart.crop_type, unitType)}
          </span>
        ),
      },
    );
    if (cyclePart.crop_type === 'MUSSEL') {
      columns.push(
        {
          key: 'spacing',
          title: translate(lang, 'Spacing'),
          render: (x: IMusselSeeding) => <span>{x.spacing}</span>,
        },
        {
          key: 'density',
          title: translate(lang, 'Density'),
          render: (x: IMusselSeeding) => <span>{SeedDensityRange(x)}</span>,
        },
      );
    }
    columns.push(
      {
        key: 'spat_size',
        title: translate(lang, 'Spat size'),
        render: (x: ISeedingResource) => {
          const lastAss = getLastAssessment(x.spat_storage?.id);
          return (
            <>
              <span>
                <SeedSizeRange data={x} />
              </span>
              {lastAss && (
                <>
                  <br />
                  <span>{' ↓ '}</span>
                  <br />
                  <span>{lastAss.shell_size.avg}</span>
                </>
              )}
            </>
          );
        },
      },
      {
        key: 'comment',
        title: translate(lang, 'Note'),
        render: (x: ISeedingResource) => (
          <div
            className='btn__modal'
            onKeyDown={() => undefined}
            onClick={openInfo.bind(this, x.comment)}
          >
            {translate(lang, 'View')}
          </div>
        ),
      },
      {
        key: 'images',
        title: translate(lang, 'Photo'),
        render: (x: ISeedingResource) =>
          x.image_pdf_files && x.image_pdf_files.length > 0 ? (
            <div
              className='btn__modal'
              onKeyDown={() => undefined}
              onClick={openImages.bind(this, x.image_pdf_files)}
            >
              {translate(lang, 'View')}
            </div>
          ) : (
            <></>
          ),
      },
      {
        key: 'weather',
        title: translate(lang, 'Weather'),
        render: (x: ISeedingResource) =>
          !x.weather_condition ? (
            <span></span>
          ) : (
            <div
              className='btn__modal'
              onClick={(e: any) =>
                setSelectedWeather(x.weather_condition ?? undefined)
              }
            >
              <img
                className='ant-image-img'
                src={weather_icon}
                alt='weather'
                style={{ maxWidth: '20px' }}
              />
              <span style={{ marginLeft: '5px' }}>
                {translate(lang, 'View')}
              </span>
            </div>
          ),
      },
      {
        key: 'direction',
        title: translate(lang, 'Direction'),
        render: (x: ISeedingResource) => (
          <div className='text-center'>{directionChar(x.direction)}</div>
        ),
      },
      {
        key: 'stage',
        title: translate(lang, 'Stage'),
        render: (x: ISeedingResource) => <StageLabelSpan stage={x.stage} />,
      },
      {
        key: 'more',
        title: translate(lang, 'More'),
        render: (x: ISeedingResource) => {
          let menuItems = [
            {
              label: translate(lang, 'Edit'),
              onClick: () => showEditModal(x),
              disabled: false,
            },
          ];
          if (isAdmin) {
            menuItems = [
              ...menuItems,
              {
                label: translate(lang, 'Delete'),
                onClick: () => showDeleteModal(x),
                disabled: !isAdmin,
              },
            ];
          }
          return <DotsMenu items={menuItems} />;
        },
      },
    );
    return columns;
  };

  return (
    <div style={{ overflow: 'auto' }}>
      <div
        className='ant-table-wrapper table mt-17'
        style={{ width: '1200px', minWidth: '100%', maxWidth: '1200px' }}
      >
        {disabled && (
          <div className='loader'>
            <Spinner />
          </div>
        )}
        <Title
          className='ml-7'
          size={6}
          color='black-3'
          align='default'
          fontWeight={700}
        >
          {translate(lang, 'Seedings')}
        </Title>
        <Table
          rowKey='id'
          className='table table--isFarm pl-0 pr-0'
          pagination={false}
          columns={tableColumns()}
          rowClassName={row => {
            const harvested = row.harvested ?? 0;
            const lineLength = row.line_length ?? 0;
            const basketCount = row.basket_count ?? 0;
            const isMusselComplete =
              cyclePart.crop_type === 'MUSSEL' && harvested >= lineLength;
            const isOysterComplete =
              cyclePart.crop_type === 'OYSTER' && harvested >= basketCount;
            return isMusselComplete || isOysterComplete
              ? 'row-line-through'
              : '';
          }}
          dataSource={
            tableRows.sort(
              (a, b) => a.planned_date_seed - b.planned_date_seed,
            ) as any
          }
        />
        {visibleEdit && (
          <SeedingModal
            visible={visibleEdit}
            title={
              !selectedItem?.id
                ? 'Update seeding data'
                : '_update_partial_line_seed'
            }
            lineData={lineData}
            onCancel={() => setVisibleEdit(false)}
            data={selectedItem}
            updateID={selectedItem?.id}
            onConfirm={() => {
              setVisibleEdit(false);
              loadLine();
            }}
            parentID={!selectedItem?.id ? undefined : selectedItem.id}
          />
        )}
        <ModalComponent
          visible={infoShow}
          onCancel={() => setInfoShow(false)}
          type=''
          title={translate(lang, 'Comment')}
          text={infoText}
        />
        {imgPdfFiles !== undefined && (
          <FilesViewModal
            visible={true}
            onClose={() => setImgPdfFiles(undefined)}
            files={imgPdfFiles}
          />
        )}
        {!!selectedItem && (
          <ModalComponent
            title={translate(
              lang,
              'Are you sure you want to delete this data?',
            )}
            text={deleteInfoText!}
            type='delete'
            visible={visibleDelete}
            disabled={disabled}
            onCancel={() => setVisibleDelete(false)}
            onConfirm={handleConfirmDelete}
          />
        )}
        {!!selectedWeather && (
          <WeatherInfoModal
            title='Weather Condition'
            data={selectedWeather}
            visible={true}
            onClose={() => setSelectedWeather(undefined)}
          />
        )}
        {!!journeyData && (
          <SpatJourneyModal
            data={journeyData}
            visible={true}
            onClose={() => setJourneyData(undefined)}
            focusLineID={lineData.id}
          />
        )}
      </div>
    </div>
  );
};

export default ExtraSeedingsTable;
